import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import { Col, Row, Button, Label, Spinner } from 'reactstrap';
import { Block, RSelect } from '../../../components/Component';
import ChannelPartnersFilter from '../../components/filter-components/ChannelPartnersFilter';
import { newRoles, HotLeadSourceFilter, HttpStatus } from '../../../utils/envConfig';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import { fetchPropertiesAdmin } from '../../../redux/actions/property';
import { fetchAgents } from '../../../redux/actions/user';
import { showToast } from '../../../utils/toast/toast';
import { useNavigate } from 'react-router-dom';
import { getAllTags } from '../../../services/tagsApi';

const NewLeadWizard = ({ phone, onBack }) => {
  const { errors, register, handleSubmit } = useForm();
  const axios = useAxiosPrivate();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authUser = useSelector((state) => state?.user?.loggedInUser);
  const authAgent = useSelector((state) => state?.user?.loggedInAgent);
  const agent = useSelector((state) => state.user?.agents);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [leadSources, setLeadSources] = useState([]);
  const [selectedLeadSource, setSelectedLeadSource] = useState(null);
  const [loading, setLoading] = useState(false);
  const [optAgents, setOptAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [activityNote, setActivityNote] = useState('');
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedChannelPartner, setSelectedChannelPartner] = useState(null);

  useEffect(() => {
    dispatch(fetchPropertiesAdmin(axios));
    fetchLeadSources();
    dispatch(fetchAgents(axios));
    fetchAllTags();
  }, []);

  useEffect(() => {
    if (agent && agent.length > 0) {
      const options = agent
        ?.filter((agents) => agents?.user?.active === true)
        ?.map((a) => ({
          value: a?.id,
          label: a?.user?.first_name + ' ' + a?.user?.last_name
        }));
      setOptAgents(options);
    }
  }, [agent]);

  const fetchLeadSources = async () => {
    try {
      const res = await axios.get(`/leadSources/hotleads?filter_out=${HotLeadSourceFilter}`);
      let filteredLeadSources = res?.data?.data || [];

      if (authUser?.role === newRoles?.Agent) {
        filteredLeadSources = filteredLeadSources.filter(
          leadSource => !leadSource.name.startsWith("Call Center")
        );
      }

      setLeadSources(filteredLeadSources);
    } catch (err) {
      console.error('Error fetching lead sources:', err);
    }
  };

  const fetchAllTags = async () => {
    try {
      const res = await axios.get(`/tags`); // Using axios directly as getAllTags needs it passed
      setAvailableTags(res?.data?.data || res?.data || []); // Adjust based on actual API response structure
    } catch (err) {
      console.error('Error fetching tags:', err);
      toast.error("Could not load tags.");
    }
  };

  const validate = () => {
    let isError = false;

    if (!selectedLeadSource && authUser?.role !== newRoles?.ChannelPartnerManager) {
      isError = true;
      toast.error('Choose Lead Source');
    }

    if (!firstName?.trim()) {
      toast.error('Enter First Name');
      isError = true;
    }

    return !isError;
  };

  const createLead = async () => {
    if (!validate()) return;

    setLoading(true);

    const agentId = selectedAgent ? selectedAgent.toString() : undefined;
    const formData = {
      lead_source: selectedLeadSource?.name,
      property_list_id: selectedProperty?.id ?? selectedProperty?._id,
      first_name: firstName,
      last_name: lastName,
      phone: phone?.trim(),
      email,
      agent_id: agentId,
      activityNote: activityNote,
      tags: selectedTags.map(tag => tag.label) // Assuming label is the tag name
    };

    const isPartnerManager = authUser?.role === newRoles?.ChannelPartnerManager;
    if (isPartnerManager || selectedChannelPartner) {
      formData.partner = isPartnerManager ? authUser?.partner?.toString() : selectedChannelPartner?.id;

      if (isPartnerManager) {
        // Ensure leadSources state is available and populated correctly
        formData.lead_source = leadSources?.find((leadSource) => leadSource?.partner === authUser?.partner?.toString())?.name;
      }
    }

    try {
      const res = await axios.post('/users/createLead', formData);
      toast.success('Created new lead successfully');
      setTimeout(() => navigate(`/user-profile/${res?.data?.id ?? res?.data?._id}/user-profile-setting`), 500);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error creating lead');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="text-center mb-4">
        <h4>Create New Lead</h4>
        <p className="text-muted">Phone: {phone}</p>
      </div>

      <Block>
        <form onSubmit={handleSubmit(createLead)}>
          <Row className="gy-4">
            {authUser?.role !== newRoles?.ChannelPartnerManager && (
              <Col sm="6">
                <div className="form-group">
                  <Label className="form-label">Choose Lead Source *</Label>
                  <RSelect
                    closeMenuOnSelect={true}
                    placeholder="Choose Lead Source"
                    options={leadSources?.map((prop) => ({
                      ...prop,
                      label: prop?.name,
                      value: prop?.name
                    })) ?? []}
                    onChange={(prop) => setSelectedLeadSource(prop)}
                    value={selectedLeadSource}
                    className="w-100"
                    isDisabled={!!selectedChannelPartner}
                  />
                </div>
              </Col>
            )} {/* This closes the conditional rendering for Lead Source */}

            {/* Channel Partner Filter */}
            {authUser?.role &&
              ![
                newRoles.Agent,
                newRoles.TeamLeader,
                newRoles.AnonymousAgent,
                newRoles.ChannelPartnerManager
              ].includes(authUser?.role) && (
                <Col sm="6">
                  <div className="form-group">
                    <Label className="form-label">Choose Channel Partner</Label>
                    <div>
                      <ChannelPartnersFilter
                        stateVar={selectedChannelPartner}
                        onChange={(partner) => {
                          if (!partner) {
                            setSelectedChannelPartner(null);
                            // Only set lead source to null if it was auto-selected by a partner
                            // This check might need refinement based on exact desired behavior
                            // For now, if clearing partner, clear lead source if it was from a partner.
                            const currentLeadSourceIsFromPartner = leadSources?.find(ls => ls.name === selectedLeadSource?.name && ls.partner === selectedChannelPartner?.id);
                            if (currentLeadSourceIsFromPartner || !selectedLeadSource) { // if it was from *this* partner or no lead source was selected
                              setSelectedLeadSource(null);
                            }
                          } else {
                            setSelectedChannelPartner(partner);
                            const foundLeadSource = leadSources?.find((ls) => ls?.partner === partner?.id);
                            if (foundLeadSource) {
                              setSelectedLeadSource({
                                ...foundLeadSource,
                                label: foundLeadSource?.name,
                                value: foundLeadSource?.name
                              });
                            } else {
                              // If no specific lead source for this partner, clear selected lead source
                              setSelectedLeadSource(null);
                            }
                          }
                        }}
                      />
                    </div>
                  </div>
                </Col>
              )}

            <Col sm="6">
              <div className="form-group">
                <label className="form-label">First Name *</label>
                <input
                  type="text"
                  placeholder="First Name"
                  className="form-control"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
            </Col>

            <Col sm="6">
              <div className="form-group">
                <label className="form-label">Last Name</label>
                <input
                  type="text"
                  placeholder="Last Name"
                  className="form-control"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
            </Col>

            <Col sm="6">
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  placeholder="Email"
                  className="form-control"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </Col>

            {authUser?.role && (
              authUser?.role !== newRoles?.Agent &&
              authUser?.role !== newRoles?.TeamLeader &&
              authUser?.role !== newRoles?.AnonymousAgent
            ) && (
                <Col sm="12">
                  <div className="form-group">
                    <Label className="form-label">Assign Agent</Label>
                    <RSelect
                      options={optAgents}
                      placeholder="Select Agent"
                      onChange={(selectedOption) => setSelectedAgent(selectedOption.value)}
                      value={optAgents.find(agent => agent.value === selectedAgent)}
                    />
                  </div>
                </Col>
              )}

            <Col sm="12">
              <div className="form-group">
                <Label className="form-label">Tags</Label>
                <RSelect
                  isMulti
                  closeMenuOnSelect={false}
                  options={availableTags?.map((tag) => ({
                    value: tag.id, // Assuming tag object has id
                    label: tag.name // Assuming tag object has name
                  })) ?? []}
                  onChange={(tags) => setSelectedTags(tags)}
                  value={selectedTags}
                  placeholder="Select Tags"
                />
              </div>
            </Col>

            <Col sm="12">
              <div className="form-group">
                <label className="form-label">Note</label>
                <textarea
                  name="notes"
                  value={activityNote}
                  onChange={(e) => setActivityNote(e.target.value)}
                  className="form-control no-resize"
                  placeholder="Add initial notes..."
                  rows="3"
                />
              </div>
            </Col>
          </Row>

          <div className="text-center mt-4">
            <Button color="light" onClick={onBack} className="me-2">
              Back
            </Button>
            <Button color="primary" type="submit" disabled={loading}>
              {loading ? (
                <Spinner style={{ width: '1rem', height: '1rem' }} />
              ) : (
                'Create Lead'
              )}
            </Button>
          </div>
        </form>
      </Block>
    </div>
  );
};

export default NewLeadWizard;